import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LifeBuoy, Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { Loader, ErrorState, EmptyState, PageHeader } from "../../components/common/States";
import TicketForm from "../../components/support/TicketForm";
import { Messages, ReplyBox } from "../../components/support/TicketThread";
import { Pill } from "../../lib/billingLabels";
import { useCompany } from "../../components/layout/CompanyLayout";
import { fmtDate } from "../../lib/labels";
import { SUPPORT_CATEGORY, SUPPORT_PRIORITY, SUPPORT_STATUS } from "../../lib/supportLabels";

export function SupportList({ companyId, base, note }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(false);
  const { data, loading, error, reload } = useApi(`/support/tickets?company_id=${companyId || ""}&status=${status}`);
  return (
    <div>
      <PageHeader title="Support" description={note} actions={<Button onClick={() => setOpen(true)} data-testid="ticket-new-btn"><Plus className="mr-1 h-4 w-4" />Nouveau ticket</Button>} />
      <select className="field-select mb-4 w-56" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="tickets-status-filter">
        <option value="">Tous les tickets</option><option value="open">En cours de traitement</option>{Object.entries(SUPPORT_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}</select>
      {loading && <Loader />}{error && <ErrorState message={error} onRetry={reload} />}
      {data && !data.length && <EmptyState icon={LifeBuoy} title="Aucun ticket" description="Ouvrez une demande, notre équipe vous répondra." />}
      <div className="space-y-2">{data?.map((t) => (
        <Link key={t.id} to={`${base}/${t.id}`} className="card-surface flex flex-wrap items-center gap-3 p-4 hover:border-blue-300" data-testid={`ticket-item-${t.number}`}>
          <span className="font-mono text-xs text-slate-500">{t.number}</span><span className="flex-1 font-medium">{t.subject}</span>
          <span className="text-xs text-slate-500">{SUPPORT_CATEGORY[t.category]} · {SUPPORT_PRIORITY[t.priority]}{companyId && t.requester_name ? ` · ${t.requester_name}` : ""} · {fmtDate(t.updated_at)}</span>
          <Pill map={SUPPORT_STATUS} value={t.status} testId={`ticket-status-${t.number}`} />
        </Link>))}</div>
      {open && <TicketForm companyId={companyId} onClose={() => setOpen(false)} onDone={(t) => { setOpen(false); navigate(`${base}/${t.id}`); }} />}
    </div>
  );
}

export const CompanySupport = () => {
  const { company } = useCompany();
  return <SupportList companyId={company.id} base="/app/support" note="Demandes de votre entreprise. Les administrateurs et directeurs voient tous les tickets de l'entreprise, les autres collaborateurs uniquement les leurs." />;
};

export const UserSupport = () => <SupportList base="/mon-espace/support" note="Vos demandes personnelles adressées à l'équipe VOXA AFRIKA." />;

export function SupportTicket({ base }) {
  const { id } = useParams();
  const { data, loading, error, reload, setData } = useApi(`/support/tickets/${id}`);
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const close = async () => { try { await api.post(`/support/tickets/${id}/close`); toast.success("Ticket clôturé"); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="ticket-detail">
      <Link to={base} className="text-sm text-blue-700" data-testid="ticket-back-link">← Mes tickets</Link>
      <div className="flex flex-wrap items-center gap-3"><h1 className="font-display text-2xl font-bold">{data.subject}</h1><Pill map={SUPPORT_STATUS} value={data.status} testId="ticket-detail-status" /></div>
      <p className="text-sm text-slate-500">{data.number} · {SUPPORT_CATEGORY[data.category]} · priorité {SUPPORT_PRIORITY[data.priority].toLowerCase()}{data.company_name ? ` · ${data.company_name}` : ""}{data.assignee_name ? ` · suivi par ${data.assignee_name}` : ""}</p>
      <Messages ticket={data} base={`/support/tickets/${id}/attachments`} viewer="customer" />
      <ReplyBox url={`/support/tickets/${id}/messages`} disabled={data.status === "closed"} onDone={(d) => (setData ? setData(d) : reload())} />
      {data.status !== "closed" && <Button variant="outline" size="sm" onClick={close} data-testid="ticket-close-btn">Clôturer (problème résolu)</Button>}
      <div className="card-surface p-4" data-testid="ticket-history"><p className="eyebrow mb-2">Historique</p>
        <ul className="space-y-1 text-xs">{data.events.map((e, i) => <li key={i}>{fmtDate(e.created_at)} · {e.type === "created" ? "Ticket ouvert" : `${SUPPORT_STATUS[e.from]?.label} → ${SUPPORT_STATUS[e.to]?.label}`}</li>)}</ul></div>
    </div>
  );
}
